/**
 * Persist run logs + presets to localStorage (mirror exportable to ./data/runs).
 * Screenshots default OFF — user must enable "save run artifacts".
 */

import type { AgentPreset, AgentRun } from "./types";

const RUNS_KEY = "agentforge.runs.v1";
const PRESETS_KEY = "agentforge.presets.v1";
const ARTIFACTS_KEY = "agentforge.save_artifacts.v1";
const CONSENT_KEY = "agentforge.trust_consent.v1";
const MAX_RUNS = 40;

export function loadRuns(): AgentRun[] {
  try {
    const raw = localStorage.getItem(RUNS_KEY);
    if (!raw) return [];
    const j = JSON.parse(raw) as AgentRun[];
    return Array.isArray(j) ? j : [];
  } catch {
    return [];
  }
}

export function saveRuns(runs: AgentRun[]): void {
  try {
    localStorage.setItem(RUNS_KEY, JSON.stringify(runs.slice(0, MAX_RUNS)));
  } catch {
    /* ignore */
  }
}

export function upsertRun(run: AgentRun): AgentRun[] {
  const prev = loadRuns().filter((r) => r.id !== run.id);
  const next = [run, ...prev].slice(0, MAX_RUNS);
  saveRuns(next);
  return next;
}

export function loadPresets(): AgentPreset[] {
  try {
    const raw = localStorage.getItem(PRESETS_KEY);
    if (!raw) return [];
    const j = JSON.parse(raw) as AgentPreset[];
    return Array.isArray(j) ? j : [];
  } catch {
    return [];
  }
}

export function savePresets(presets: AgentPreset[]): void {
  try {
    localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
  } catch {
    /* ignore */
  }
}

export function addPreset(name: string, goal: string): AgentPreset[] {
  const p: AgentPreset = {
    id: `preset_${Date.now()}`,
    name: name.trim() || "Untitled playbook",
    goal: goal.trim(),
    createdAt: new Date().toISOString(),
  };
  const next = [p, ...loadPresets()].slice(0, 50);
  savePresets(next);
  return next;
}

export function getSaveArtifacts(): boolean {
  try {
    return localStorage.getItem(ARTIFACTS_KEY) === "1";
  } catch {
    return false;
  }
}

export function setSaveArtifacts(on: boolean): void {
  try {
    localStorage.setItem(ARTIFACTS_KEY, on ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function getTrustConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === "1";
  } catch {
    return false;
  }
}

export function setTrustConsent(ok: boolean): void {
  try {
    localStorage.setItem(CONSENT_KEY, ok ? "1" : "0");
  } catch {
    /* ignore */
  }
}

/** Export run as downloadable JSON (user can place under ./data/runs). */
export function exportRunJson(run: AgentRun): string {
  return JSON.stringify(run, null, 2);
}

export function downloadRun(run: AgentRun): void {
  const blob = new Blob([exportRunJson(run)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `agentforge-run-${run.id}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}
